<template>
  <AppLayout>
    <div class="group-members-view">
      <div class="header">
        <h1>Group Members</h1>
        <button v-if="canManageGroup" @click="addMember" class="btn btn-primary">
          Add Member
        </button>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="loading-state">
        <p>Loading members...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="error-state">
        <p class="error">{{ error }}</p>
        <button @click="retry" class="btn btn-secondary">Retry</button>
      </div>

      <!-- Members List -->
      <div v-else class="members-list">
        <div v-if="members.length === 0" class="empty-state">
          <p>No members in this group yet.</p>
          <button v-if="canManageGroup" @click="addMember" class="btn btn-primary">
            Add First Member
          </button>
        </div>

        <div v-else class="members-table">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Joined</th>
                <th v-if="canManageGroup">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="member in members" :key="member.user_id">
                <td>{{ memberDisplayName(member) }}</td>
                <td>{{ member.email }}</td>
                <td>
                  <span :class="['role-badge', isGroupOwner(member) ? 'role-owner' : `role-${member.role}`]">
                    {{ isGroupOwner(member) ? 'owner' : member.role }}
                  </span>
                </td>
                <td>{{ formatDate(member.joined_at) }}</td>
                <td v-if="canManageGroup">
                  <button
                    v-if="!isGroupOwner(member)"
                    @click="removeMemberConfirm(member)"
                    class="btn btn-sm btn-danger"
                  >
                    Remove
                  </button>
                  <span v-else class="text-muted">-</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import { onMounted, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useGroupsStore } from '../../stores/groups';
import { storeToRefs } from 'pinia';
import AppLayout from '../../components/layout/AppLayout.vue';
import { memberDisplayName, type GroupMember } from '../../types/group';

const router = useRouter();
const route = useRoute();
const groupsStore = useGroupsStore();

const props = defineProps<{
  id: string
}>();

const { members, isLoading, error, canManageGroup, currentGroup } = storeToRefs(groupsStore);

function isGroupOwner(m: GroupMember) {
  return currentGroup.value?.owner_id === m.user_id
}

const groupId = computed(() => parseInt(props.id || route.params.id as string));

onMounted(async () => {
  await loadMembers();
});

const loadMembers = async () => {
  try {
    await groupsStore.fetchMembers(groupId.value);
  } catch (err) {
    console.error('Failed to load members:', err);
  }
};

const addMember = () => {
  router.push(`/groups/${groupId.value}/members/add`);
};

const removeMemberConfirm = async (member: GroupMember) => {
  if (confirm(`Are you sure you want to remove ${memberDisplayName(member)} from this group?`)) {
    try {
      await groupsStore.removeMember(groupId.value, member.user_id);
    } catch (err) {
      console.error('Failed to remove member:', err);
    }
  }
};

const retry = () => {
  loadMembers();
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};
</script>

<style scoped>
.group-members-view {
  padding: 2rem;
  max-width: 1000px;
  margin: 0 auto;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.header h1 {
  margin: 0;
}

.loading-state,
.error-state,
.empty-state {
  text-align: center;
  padding: 3rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.error {
  color: var(--color-error);
  margin-bottom: 1rem;
}

.members-table {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.members-table table {
  width: 100%;
  border-collapse: collapse;
}

.members-table th,
.members-table td {
  padding: 1rem;
  text-align: left;
  border-bottom: 1px solid var(--color-border);
}

.members-table th {
  background: var(--color-background);
  font-weight: 600;
  color: var(--color-text);
}

.members-table tbody tr:hover {
  background: var(--color-background-light);
}

.role-badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.875rem;
  text-transform: capitalize;
}

.role-owner {
  background: var(--color-warning-light);
  color: var(--color-warning);
}

.role-admin {
  background: var(--color-info-light);
  color: var(--color-info);
}

.role-member {
  background: var(--color-success-light);
  color: var(--color-success);
}

.text-muted {
  color: var(--color-text-secondary);
}

.btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  transition: background-color 0.3s;
}

.btn-primary {
  background: var(--color-primary);
  color: white;
}

.btn-primary:hover {
  background: var(--color-primary-dark);
}

.btn-secondary {
  background: var(--color-secondary);
  color: white;
}

.btn-danger {
  background: var(--color-error);
  color: white;
}

.btn-danger:hover {
  background: var(--color-error-dark);
}

.btn-sm {
  padding: 0.25rem 0.75rem;
  font-size: 0.875rem;
}
</style>
